import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, orderBy, limit } from "firebase/firestore";
import { db } from "./firebase";
import { useEffectiveTenantId } from "./impersonation";

export type CustomerRow = {
  id: string;
  email: string | null;
  displayName: string | null;
  role: string;
  createdAt: Date | null;
};

export function useTenantCustomers() {
  const tenantId = useEffectiveTenantId();
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "tenants", tenantId, "users"),
      orderBy("createdAt", "desc")
    );
    return onSnapshot(
      q,
      (snap) => {
        setCustomers(
          snap.docs
            .map((d) => {
              const x = d.data();
              return {
                id: d.id,
                email: (x.email as string | null) ?? null,
                displayName: (x.displayName as string | null) ?? null,
                role: (x.role as string) ?? "customer",
                createdAt: (x.createdAt as { toDate?: () => Date })?.toDate?.() ?? null
              };
            })
            .filter((c) => c.role !== "tenant_admin" && c.role !== "platform_admin")
        );
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [tenantId]);

  return { customers, loading };
}

export type CustomerDetail = {
  loading: boolean;
  user: CustomerRow | null;
  totalPurchases: number;
  totalSpent: number;
  ticketsUnredeemed: number;
  ticketsRedeemed: number;
  rewardsClaimed: number;
  recentPurchases: Array<{
    id: string;
    amount: number;
    status: string;
    createdAt: Date | null;
    voucherStoragePath: string | null;
    imageMissing: boolean;
  }>;
};

export function useCustomerDetail(uid: string | null): CustomerDetail {
  const tenantId = useEffectiveTenantId();
  const [detail, setDetail] = useState<CustomerDetail>({
    loading: true,
    user: null,
    totalPurchases: 0,
    totalSpent: 0,
    ticketsUnredeemed: 0,
    ticketsRedeemed: 0,
    rewardsClaimed: 0,
    recentPurchases: []
  });

  useEffect(() => {
    if (!tenantId || !uid) {
      setDetail((d) => ({ ...d, loading: false }));
      return;
    }
    const tid = tenantId;
    const unsubs: Array<() => void> = [];

    unsubs.push(
      onSnapshot(
        query(collection(db, "tenants", tid, "users"), where("uid", "==", uid)),
        (snap) => {
          const d = snap.docs[0]?.data();
          setDetail((s) => ({
            ...s,
            user: d
              ? {
                  id: snap.docs[0].id,
                  email: (d.email as string) ?? null,
                  displayName: (d.displayName as string) ?? null,
                  role: (d.role as string) ?? "customer",
                  createdAt: (d.createdAt as { toDate?: () => Date })?.toDate?.() ?? null
                }
              : null,
            loading: false
          }));
        }
      )
    );

    unsubs.push(
      onSnapshot(
        query(
          collection(db, "tenants", tid, "purchases"),
          where("userId", "==", uid),
          orderBy("createdAt", "desc"),
          limit(10)
        ),
        (snap) => {
          const data = snap.docs.map((d) => d.data());
          const total = data.reduce((acc, x) => acc + (typeof x.amount === "number" ? x.amount : 0), 0);
          setDetail((s) => ({
            ...s,
            totalPurchases: snap.size,
            totalSpent: total,
            recentPurchases: snap.docs.map((d) => {
              const x = d.data();
              return {
                id: d.id,
                amount: typeof x.amount === "number" ? x.amount : 0,
                status: (x.status as string) ?? "—",
                createdAt: (x.createdAt as { toDate?: () => Date })?.toDate?.() ?? null,
                voucherStoragePath: (x.voucherStoragePath as string | undefined) ?? null,
                imageMissing: x.imageMissing === true
              };
            })
          }));
        }
      )
    );

    unsubs.push(
      onSnapshot(
        query(collection(db, "tenants", tid, "tickets"), where("userId", "==", uid)),
        (snap) => {
          const unred = snap.docs.filter((d) => d.data().redeemed === false).length;
          setDetail((s) => ({
            ...s,
            ticketsUnredeemed: unred,
            ticketsRedeemed: snap.size - unred
          }));
        }
      )
    );

    unsubs.push(
      onSnapshot(
        query(collection(db, "tenants", tid, "rewards"), where("userId", "==", uid)),
        (snap) => setDetail((s) => ({ ...s, rewardsClaimed: snap.size }))
      )
    );

    return () => unsubs.forEach((u) => u());
  }, [tenantId, uid]);

  return detail;
}
